import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import {
  areaCalc,
  CHP_ACTION_ENTRY_TYPES,
  getCustomCorrosionRegionArea,
  getInspectionDate,
  getTotalCostFromProjectCostData,
  OpportunityCostProps,
} from 'features/fde'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import sortBy from 'lodash/sortBy'
import {
  AssetHealthActionStatus,
  AssetHealthDataFrame,
  AssetHealthPlanRemainingLifeData,
  AssetOutageConfig,
  CustomCorrosionRegion,
  FailureProbability,
  FDECustomerUnit,
  GeckoAssetHealthAction,
  GeckoAssetHealthPlan,
  GeckoComponent,
  GeckoComponentModel,
  GeckoEntry,
  GeckoEntryBase,
  GeckoInspection,
  GeckoMarkupLayer,
  GeckoMeasurementUnit,
  GeckoSite,
  HealthPlanAction,
  HealthPlanActionData,
  LinkedHealthPlanAction,
  Outage,
  PipeWrapRegion,
  PlanAndActions,
  ProjectCostData,
  RolodexEntryJson,
  RolodexEntryPutJson,
  RolodexEntryType,
  TimeUnit,
} from 'types'
import { displayCurrency } from 'utils/displayCurrency'
import { DATE_FORMAT } from 'utils/fde/constants'
import { v4 as uuidv4 } from 'uuid'

dayjs.extend(duration)

/**
 *
 */
/**
 * Creates a new health plan action entry for a given plan
 *
 * @param {string} [name] - The name of the action.
 * @param {dayjs.Dayjs} [date] - The date of the action.
 * @param {GeckoEntryBase<AhpPlan>} [plan] - The health plan associated with the action.
 * @param {RolodexEntryType | string} [actionType] - The type of action, may be either an entry type or arbitrary string
 * @param {GeckoSite} [site] - The site where the action is taking place.
 * @param {string} [linkedEntryId] - The ID of a linked entry for the action, if any.
 * @param {GeckoComponent} [selectedComponent] - The selected component for the action, if any.
 * @param {GeckoEntry} [selectedUnit] - The selected unit for the action, if any. If the selectedComponent is given, the unit will be inferred from the component.
 * @param {number} [actionCost] - The cost of the action. If null, the cost will be calculated based on the linked entry.
 *
 * @returns {RolodexEntryPutJson} The newly created health plan action entry.
 */
export function getHealthPlanActionEntry({
  name,
  date,
  plan,
  actionType,
  linkedEntryId,
  selectedComponent,
  actionCost,
  site,
  selectedUnit,
  costTypeHierarchy,
  selectedAnnotationIds,
  fixedAreaFt2,
  isFullReplacement,
  actionTags,
  deficiencyIds,
}: {
  name: string
  date: dayjs.Dayjs
  plan: GeckoAssetHealthPlan
  actionType: RolodexEntryType | string
  site: GeckoSite
  linkedEntryId?: string
  selectedComponent?: GeckoComponent
  selectedUnit?: GeckoEntry
  actionCost?: number
  costTypeHierarchy?: string[]
  selectedAnnotationIds?: string[]
  fixedAreaFt2?: number
  isFullReplacement?: boolean
  actionTags?: string[]
  deficiencyIds?: string[]
}): RolodexEntryPutJson {
  return {
    id: uuidv4(),
    type: RolodexEntryType.COMPONENT_HEALTH_PLAN_ACTION,
    data: {
      name,
      actionDate: date.format(DATE_FORMAT),
      actionStatus: AssetHealthActionStatus.PENDING,
      actionType,
      actionCost,
      created_time: dayjs().valueOf(),
      implemented: false,
      costTypeHierarchy,
      selectedAnnotationIds,
      fixedAreaFt2,
      isFullReplacement,
      actionTags,
      deficiencyIds,
    },
    tags: omitBy(
      {
        [RolodexEntryType.SITE]: site.id,
        [RolodexEntryType.ORGANIZATION]: site.tags.organization,
        [RolodexEntryType.COMPONENT_HEALTH_PLAN]: plan.id,
        [RolodexEntryType.UNIT]:
          selectedUnit?.id ?? selectedComponent?.tags.unit ?? null,
        [RolodexEntryType.COMPONENT]: selectedComponent?.id ?? null,
        [actionType]: linkedEntryId ?? null,
      },
      isNil
    ),
    index: {},
  }
}

export function getHealthPlanEntry({
  name,
  preferred,
  site,
  failureProbabilities,
}: {
  name: string
  preferred: boolean
  site: GeckoSite
  failureProbabilities?: FailureProbability[]
}): RolodexEntryPutJson {
  return {
    id: uuidv4(),
    type: RolodexEntryType.COMPONENT_HEALTH_PLAN,
    data: {
      name,
      created_time: dayjs().valueOf(),
      preferred,
      failureProbabilities,
    },
    tags: {
      [RolodexEntryType.SITE]: site.id,
      [RolodexEntryType.ORGANIZATION]: site.tags.organization,
    },
    index: {},
  }
}

export function opportunityDiscountPropsFromFDECustomerUnit(
  fdeCustomerUnit: GeckoEntryBase<FDECustomerUnit> | undefined,
  projectDate: dayjs.Dayjs
): OpportunityCostProps | undefined {
  return fdeCustomerUnit?.data.opportunity_cost_discount
    ? {
        discountPercentage: fdeCustomerUnit.data.opportunity_cost_discount,
        projectDate,
      }
    : undefined
}

export function getLinkedEntryIdFromAction(
  action: GeckoAssetHealthAction
): string | undefined {
  return (
    Object.entries(action.tags).find(
      ([key, value]) =>
        CHP_ACTION_ENTRY_TYPES.includes(key as RolodexEntryType) &&
        value !== null
    )?.[1] ?? undefined
  )
}

/**
 *
 */
/**
 * Generates health plan action data based on the provided parameters.
 *
 * @param {GeckoEntryBase<AhpAction>} params.ahpAction - The AHP action entry.
 * @param {GeckoEntry} [params.linkedEntry] - The linked entry for the action Ex) repair-plan entry.
 * @param {readonly GeckoMarkupLayer[]} params.markupLayers - All markup layers in scope, used to find the affected areas for the linked entries .
 * @param {GeckoComponent} [params.selectedComponent] - The selected component, if any.
 * @param {GeckoComponentModel} [params.selectedComponentModel] - The selected component model, if any.
 * @param {GeckoEntryBase<FDECustomerUnit>} [params.fdeCustomerUnit] - The FDE customer unit, if any. Used to get the cost properties for the unit
 *
 * @returns {HealthPlanActionData} The generated health plan action data, including cost total, area, and linked entry data.
 */
export function getHealthPlanActionData({
  ahpAction,
  linkedEntry,
  markupLayers,
  selectedComponent,
  selectedComponentModel,
  fdeCustomerUnit,
}: {
  ahpAction: GeckoAssetHealthAction
  linkedEntry?: GeckoEntry
  markupLayers: readonly GeckoMarkupLayer[]
  selectedComponent?: GeckoComponent
  selectedComponentModel?: GeckoComponentModel
  fdeCustomerUnit?: GeckoEntryBase<FDECustomerUnit>
}): HealthPlanActionData {
  if (!linkedEntry) {
    return {
      costTotal: ahpAction.data.actionCost ?? null,
      linkedEntry: undefined,
      actionType: ahpAction.data.actionType,
    }
  }

  const linkedEntryData: LinkedHealthPlanAction = {
    id: linkedEntry.id,
    data: linkedEntry.data,
    type: linkedEntry.type,
  }
  let costTotal: number | null = ahpAction.data.actionCost ?? null
  let area: number | undefined

  const opportunityCostProps = opportunityDiscountPropsFromFDECustomerUnit(
    fdeCustomerUnit,
    dayjs(ahpAction.data.actionDate, DATE_FORMAT)
  )
  const costInfo = linkedEntry.data?.cost as ProjectCostData | undefined

  if (linkedEntry.type === RolodexEntryType.REPAIR_PLAN) {
    const filteredMarkupLayers = linkedEntry
      ? markupLayers.filter(
          (ml) => ml.tags[RolodexEntryType.REPAIR_PLAN] === linkedEntry?.id
        )
      : undefined
    const repairArea =
      filteredMarkupLayers && selectedComponentModel
        ? filteredMarkupLayers.reduce((agg, markupLayer) => {
            return (
              agg +
              areaCalc(markupLayer.data.allPrimitives, selectedComponentModel)
            )
          }, 0)
        : 0
    const repairCost = costInfo
      ? getTotalCostFromProjectCostData({
          ...costInfo,
          area: repairArea,
          opportunityCostProps,
        })
      : null

    linkedEntryData['markupLayers'] = filteredMarkupLayers
    costTotal = costTotal ?? repairCost
    area = repairArea
  } else if (linkedEntry.type === RolodexEntryType.DERATING_SCENARIO) {
    const customCorrosionRegions = linkedEntry.data.regions as
      | CustomCorrosionRegion[]
      | undefined
    const corrosionRegionsArea =
      customCorrosionRegions && selectedComponent && selectedComponentModel
        ? getCustomCorrosionRegionArea({
            customCorrosionRegions,
            selectedComponent,
            selectedComponentModel,
          })
        : null
    const corrosionRegionCost = costInfo
      ? getTotalCostFromProjectCostData({
          ...costInfo,
          area: corrosionRegionsArea ?? undefined,
          opportunityCostProps,
        })
      : null

    area = corrosionRegionsArea ?? undefined
    costTotal = costTotal ?? corrosionRegionCost
  } else if (linkedEntry.type === RolodexEntryType.PIPE_WRAP) {
    const wrapMarkupLayerEntries = mapWrapRegionstoMarkupLayers({
      wrapRegions: linkedEntry.data.wrapRegions as PipeWrapRegion[],
      selectedComponent,
      selectedComponentModel,
      wrapDate: dayjs(ahpAction.data.actionDate, DATE_FORMAT),
    })

    const wrapMarkupLayers = wrapMarkupLayerEntries.map((layer) =>
      GeckoMarkupLayer.CreateFromJSON(layer as RolodexEntryJson)
    ) // Need this cast because index and generation are required in type, despite not being needed by constructor. TODO: Should update constructor
    const wrappedArea = selectedComponentModel
      ? wrapMarkupLayers.reduce((acc, layer) => {
          return (
            acc + areaCalc(layer.data.allPrimitives, selectedComponentModel)
          )
        }, 0)
      : null

    const wrapCost = costInfo
      ? getTotalCostFromProjectCostData({
          ...costInfo,
          area: wrappedArea ?? undefined,
          opportunityCostProps,
        })
      : null

    area = wrappedArea ?? undefined
    costTotal = costTotal ?? wrapCost
    linkedEntryData['markupLayers'] = wrapMarkupLayers
  } else if (linkedEntry.type === RolodexEntryType.REPLACEMENT_PLAN) {
    // TODO: Evaluate if this true for all of our geometries
    const replacementArea: number | null = selectedComponentModel
      ? selectedComponentModel.componentBounds.height.convertTo(
          GeckoMeasurementUnit.Feet
        ).num *
        selectedComponentModel.componentBounds.width.convertTo(
          GeckoMeasurementUnit.Feet
        ).num
      : null

    const replacementCost = costInfo
      ? getTotalCostFromProjectCostData({
          ...costInfo,
          area: replacementArea ?? undefined,
          opportunityCostProps,
        })
      : null
    area = replacementArea ?? undefined
    costTotal = costTotal ?? replacementCost
  }

  return {
    componentId: selectedComponent?.id,
    componentName: selectedComponent?.data.name,
    componentType: selectedComponentModel?.type,
    costTotal,
    area,
    linkedEntry: linkedEntryData,
    actionType: linkedEntry.type,
  }
}

/**
 * Return a new object that only contains the keys/val pairs that return true from the callback
 * NOTE: Lodash has a similar function, but running into type issues with the current version 2024.07.16
 */
export const filterObject = (
  obj: Record<string, unknown>,
  callback: (value: unknown) => boolean = (value) => !!value
) => {
  const result: Record<string, unknown> = {}
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && callback(obj[key])) {
      result[key] = obj[key]
    }
  }
  return result
}

/**
 * Check if a datetime occurs within a given interval of two other datetimes, inclusive.
 * @param date Date to check in interval
 * @param start Start date
 * @param end End date
 */
export function dateInInterval(
  date: dayjs.Dayjs,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs
): boolean {
  return (
    (date.isAfter(start) && date.isBefore(end)) ||
    date.isSame(start) ||
    date.isSame(end)
  )
}

/**
 * Get the min or max value from a 2D array of numbers
 * @param array 2D array of number data
 * @param boundType 'max' or 'min'
 * @returns Value for the given bound, or null if all values are unbounded or NaN
 */
export function getBoundFrom2DArray(
  array: number[][] | undefined,
  boundType: 'max' | 'min'
): number | null {
  const bound = getBoundIdxFrom2DArray(array, boundType)

  return !isNil(bound) && array ? array[bound[0]][bound[1]] : null
}

/**
 * Same as `getBoundFrom2DArray` but returns the index of the bound instead of the value
 * @returns 2D Index of the first-found value for the given bound
 */
export function getBoundIdxFrom2DArray(
  array: number[][] | undefined,
  boundType: 'max' | 'min'
): [number, number] | null {
  const boundFn = boundType === 'max' ? Math.max : Math.min
  const startBound = boundType === 'max' ? -Infinity : Infinity
  let bound = startBound

  const boundIndices = array?.reduce((boundIdx, row, idx) => {
    const numRow = row.map((val) => (Number.isNaN(val) ? startBound : val))
    const currBound = boundFn(bound, ...numRow)
    if (currBound !== bound) {
      bound = currBound
      return [idx, numRow.indexOf(currBound)] as const
    }
    return boundIdx
  }, [])

  return !isNil(boundIndices) && boundIndices.length === 2
    ? (boundIndices as [number, number])
    : null
}

export const getCostFromLinkedActions = (
  actionObjects: HealthPlanAction[]
): number => {
  return (
    actionObjects.reduce(
      (acc, cur) => acc + (cur.actionData.costTotal ?? 0),
      0
    ) ?? 0
  )
}

export const getMeanCostFromLinkedActions = (
  actionObjects: HealthPlanAction[],
  years: number,
  locale: string
): string => {
  if (years === 0) {
    return displayCurrency(0, locale)
  }

  const sumCost =
    actionObjects.reduce(
      (acc, cur) => acc + (cur.actionData.costTotal ?? 0),
      0
    ) ?? 0

  return displayCurrency(sumCost / years, locale)
}

/**
 * Prioritizes plan creation date, and
 * falls back to the earliest action creation date, or otherwise today.
 * @param selectedPlanAndActions Plan and actions to check for creation dates
 * @returns The earliest creation date of the plan or actions, or otherwise today
 */
export const getEarliestCreationDate = (
  selectedPlanAndActions: PlanAndActions | undefined
) => {
  return (
    dayjs(
      selectedPlanAndActions?.plan.data.created_time ??
        Math.min(
          ...(selectedPlanAndActions?.actions.map(
            (action) => action.action.data?.created_time
          ) || [])
        )
    ) ?? dayjs()
  )
}

export const isTimeUnit = isEnum<TimeUnit>(TimeUnit)

/**
 * Generates a list of outage start and end periods, in chronological order, based on the given outage configuration.
 */
export function generateOutagePeriods(
  assetOutageConfig: AssetOutageConfig
): Outage[] {
  if (
    !![
      assetOutageConfig.yearOfRetirement,
      assetOutageConfig.assetOutage?.data.outage_start_date,
      assetOutageConfig.assetOutage?.data.outage_interval_unit,
      assetOutageConfig.assetOutage?.data.outage_length_unit,
    ].find((val) => isNil(val)) ||
    !assetOutageConfig.assetOutage?.data.outage_interval ||
    !assetOutageConfig.assetOutage?.data.outage_length
  ) {
    return new Array<Outage>()
  }
  const yearOfRetirementDate = dayjs(assetOutageConfig.yearOfRetirement, 'YYYY')

  const outages: Outage[] = []
  const outageInterval = assetOutageConfig.assetOutage?.data
    .outage_interval as number
  const outageIntervalUnit = assetOutageConfig.assetOutage?.data
    .outage_interval_unit as TimeUnit
  const outageLength = assetOutageConfig.assetOutage?.data
    .outage_length as number
  const outageLengthUnit = assetOutageConfig.assetOutage?.data
    .outage_length_unit as TimeUnit
  let currOutageDate = dayjs(
    assetOutageConfig.assetOutage?.data.outage_start_date as string,
    DATE_FORMAT
  )

  while (yearOfRetirementDate.isAfter(currOutageDate)) {
    const endDate = currOutageDate.add(outageLength, outageLengthUnit)
    outages.push({
      startDate: currOutageDate,
      endDate,
    })
    currOutageDate = currOutageDate.add(outageInterval, outageIntervalUnit)
  }

  return outages
}

export function getAssetHealthPlanRemainingLifeData({
  ahpDataFrames,
  yearOfRetirement,
  initialRemainingLife,
  selectedInspection,
}: {
  ahpDataFrames: readonly AssetHealthDataFrame[] | undefined
  yearOfRetirement: dayjs.Dayjs | undefined
  initialRemainingLife: number | null
  selectedInspection: GeckoInspection | undefined
}): AssetHealthPlanRemainingLifeData[] | undefined {
  const baseInspectionDate = getInspectionDate({
    inspection: selectedInspection,
    inspectionSlug: selectedInspection?.id,
  })

  if (!ahpDataFrames || !baseInspectionDate || !initialRemainingLife) {
    return undefined
  }

  const sortDataFrames = sortBy(
    ahpDataFrames,
    (dataframe) => dataframe.spanToDate
  )

  const data: AssetHealthPlanRemainingLifeData[] = [
    {
      date: baseInspectionDate,
      remainingLife: initialRemainingLife,
    },
  ]

  const rlData = sortDataFrames.reduce((agg, dataframe) => {
    const lastDataItem = data[data.length - 1]
    if (!lastDataItem) {
      return agg
    }

    const lastActionDate = lastDataItem.date
    const actionDate = dayjs(dataframe.spanToDate, DATE_FORMAT)
    const remainingLifeDecrease = actionDate.diff(
      lastActionDate,
      TimeUnit.YEAR,
      true
    )
    const preActionRL = lastDataItem.remainingLife - remainingLifeDecrease
    agg.push({
      date: actionDate,
      remainingLife: preActionRL,
    })

    const postActionRl = getBoundFrom2DArray(dataframe.rl, 'min') ?? 0

    agg.push({
      componentHealthPlanActionId: dataframe.componentHealthPlanActionId,
      date: actionDate,
      remainingLife: postActionRl,
      remainingLifeAdded: postActionRl - preActionRL,
    })

    return agg
  }, data)

  if (yearOfRetirement) {
    const yearsUntilRetirement = yearOfRetirement.diff(
      rlData[rlData.length - 1].date,
      'years',
      true
    )
    rlData.push({
      date: yearOfRetirement,
      remainingLife:
        rlData[rlData.length - 1].remainingLife - yearsUntilRetirement,
    })
  }

  return rlData
}

/**
 * We cannot simply add the remaining life in years to calculate failure date, as date math over years will fallback on integer math.
 * The reason for this is pretty fundamental to date math. See the blog post below, it's for moment js but equally as relevant:
 * https://github.com/moment/moment/issues/2430#issuecomment-113832948
 * So we convert these decimal years to days for a better approximation of the date of failure.
 * @param rl Remaining life in years
 * @returns Duration object of remaining life in days
 */
export function getFailureDateForAssetHealthPlan(
  remainingLifeData: AssetHealthPlanRemainingLifeData[]
): dayjs.Dayjs | null {
  const remainingLifeToDuration = (rl: number) => {
    const days = dayjs.duration(rl, TimeUnit.YEAR).as(TimeUnit.DAY)
    return dayjs.duration(days, TimeUnit.DAY)
  }

  const pointOfFailure = remainingLifeData.find((graphData, idx) => {
    const negativeRL = graphData.remainingLife < 0
    if (!negativeRL) {
      return false
    }

    const nextDate = remainingLifeData[idx + 1]
      ? remainingLifeData[idx + 1].date
      : null
    return (
      graphData.date.isSame(dayjs()) ||
      (graphData.date.isBefore(dayjs()) && nextDate?.isAfter(dayjs()))
    )
  })

  if (!pointOfFailure) {
    const lastItem = remainingLifeData[remainingLifeData.length - 1]
    if (!lastItem) {
      return null
    }

    const expirationDate = lastItem.date.add(
      remainingLifeToDuration(lastItem.remainingLife)
    )
    return expirationDate
  }

  return pointOfFailure.date.add(
    remainingLifeToDuration(pointOfFailure.remainingLife)
  )
}

export function selectedDataFrameCallout(
  selectedDataFrame: AssetHealthDataFrame | undefined
): string {
  if (!selectedDataFrame) {
    return 'Viewing original inspection data'
  }

  const date = dayjs(selectedDataFrame.spanToDate, DATE_FORMAT)
  return `Viewing data as of: ${date.format('MMM YYYY')}`
}

/**
 * Used to generate markup layers of pipe wrap regions at runtime.
 * TODO: Should we group the regions into one markup layer rather than creating on per section?
 * @returns Array of markup layers for pipe wrap regions, one for each region
 */
export function mapWrapRegionstoMarkupLayers({
  wrapRegions,
  selectedComponent,
  selectedComponentModel,
  wrapDate,
}: {
  wrapRegions: PipeWrapRegion[]
  selectedComponent: GeckoComponent | undefined
  selectedComponentModel: GeckoComponentModel | undefined
  wrapDate?: dayjs.Dayjs
}): RolodexEntryPutJson[] {
  if (!selectedComponent || !selectedComponentModel) {
    return []
  }
  const xStartBound = selectedComponentModel.componentBounds.x.start.num
  const xEndBound = selectedComponentModel.componentBounds.x.end.num
  const xMeasurementUnit = selectedComponentModel.componentBounds.x.unit.id

  return wrapRegions.map((region) => {
    return {
      id: region.id,
      type: RolodexEntryType.MARKUP_LAYER,
      data: {
        name: `Pipe Wrap Section${wrapDate ? ' - ' + wrapDate.format(DATE_FORMAT) : ''}`,
        color: 'blue',
        geometry: {
          x_start: xStartBound,
          y_start: region.regionStart,
          x_end: xEndBound,
          y_end: region.regionEnd,
          x_measurement_unit: xMeasurementUnit,
          y_measurement_unit: region.measurementUnit,
          type: 'rect',
        },
        primitives: {
          [region.id]: {
            x_start: xStartBound,
            y_start: region.regionStart,
            x_end: xEndBound,
            y_end: region.regionEnd,
            kind: 'rect',
          },
        },
      },
      tags: {
        component: selectedComponent?.id ?? null,
        ...selectedComponent?.tags,
      },
      index: {},
    }
  })
}

export function createPipeWrap({
  id,
  name,
  wrapRegions,
  selectedComponent,
  costData,
}: {
  id: string
  name: string
  wrapRegions: PipeWrapRegion[]
  selectedComponent: GeckoComponent | undefined
  costData?: ProjectCostData
}): RolodexEntryPutJson {
  return {
    id,
    type: RolodexEntryType.PIPE_WRAP,
    data: {
      name,
      create_time: dayjs().valueOf(),
      wrapRegions,
      cost: costData,
    },
    tags: {
      ...selectedComponent?.raw.tags,
      component: selectedComponent?.id ?? null,
    },
    index: {},
  }
}

export function createAssetReplacement({
  id,
  name,
  selectedComponent,
  costData,
}: {
  id: string
  name: string
  selectedComponent: GeckoComponent | undefined
  costData?: ProjectCostData
}): RolodexEntryPutJson {
  return {
    id,
    type: RolodexEntryType.REPLACEMENT_PLAN,
    data: {
      name,
      create_time: dayjs().valueOf(),
      cost: costData,
    },
    tags: {
      ...selectedComponent?.raw.tags,
      component: selectedComponent?.id ?? null,
    },
    index: {},
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function isEnum<T>(enumObj: any): (value: any) => value is T {
  return (value: any): value is T => {
    return Object.values<T>(enumObj).includes(value)
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
